import React, { useEffect, useState } from 'react';
import { List, Drawer } from '@material-ui/core';
import clsx from 'clsx';
import { makeStyles, useTheme } from '@material-ui/core';
import { styles } from './sideNav.styles';
import SideNavItem from './components/sideNavItem';
import SideNavFooter from './components/sideNavFooter';
import { useSelector } from 'react-redux';
import { RootStoreModel } from '../../../../redux';
import { mapMenuItems } from '../../../../helpers/sideNavHelper';

export interface SideNavProps {
  autoCollapse: boolean;
  headerClicked: boolean;
}

export const SideNav: React.FC<SideNavProps> = ({ autoCollapse = true, headerClicked }) => {
  const currentTheme = useTheme();
  const useStyles = makeStyles(styles(currentTheme));
  const classes = useStyles();

  const [sideNavOpen, setSideNavOpen] = useState<boolean>(false);
  const [sideNavAutoCollapse, setSideNavAutoCollapse] = useState<boolean>(autoCollapse);

  const profile = useSelector((state: RootStoreModel) => state.profile);

  useEffect(() => {
    setSideNavAutoCollapse(autoCollapse);
  }, [autoCollapse]);

  useEffect(() => {
    if (sideNavOpen && sideNavAutoCollapse) {
      setSideNavOpen(false);
    } else if (!sideNavOpen && !sideNavAutoCollapse) {
      setSideNavOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sideNavAutoCollapse]);

  const handleCollapse = React.useCallback(() => {
    if (sideNavOpen && sideNavAutoCollapse) {
      setSideNavOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sideNavOpen]);

  const handleOpen = React.useCallback(() => {
    if (!sideNavOpen && sideNavAutoCollapse) {
      setSideNavOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sideNavOpen]);

  const sideNavItems = React.useMemo(() => {
    return (
      <List component="nav" className={classes.sideNav} disablePadding>
        {mapMenuItems(profile.user?.menuItems.sort((a, b) => (a.order > b.order ? 1 : -1))).map(
          (item, index) => (
            <SideNavItem {...item} key={index} sideNavOpen={sideNavOpen} />
          )
        )}
      </List>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sideNavOpen, profile]);

  return (
    <>
      <Drawer
        open={true}
        transitionDuration={10}
        PaperProps={{ variant: 'outlined' }}
        BackdropProps={{ open: false }}
        variant={sideNavAutoCollapse === true ? 'temporary' : 'permanent'}
        disableEnforceFocus={sideNavAutoCollapse}
        className={clsx(classes.drawerPaper, {
          'drawerPaper--open': sideNavOpen,
          'drawerPaper--closed': !sideNavOpen
        })}
        classes={{
          paper: clsx(classes.drawerPaper, {
            'drawerPaper--open': sideNavOpen,
            'drawerPaper--closed': !sideNavOpen
          })
        }}
        onMouseEnter={handleOpen}
        onMouseLeave={handleCollapse}
      >
        <div className={classes.sideNavContainer}>
          {sideNavItems}
          <SideNavFooter open={sideNavOpen} />
        </div>
      </Drawer>
    </>
  );
};

export default React.memo(SideNav);
